import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";

import { parseStringBoolean } from "./lib/browser-utils";

/**
 * Query params carrying viewer-entered text that can name a person: the outcomes
 * entity search writes a picked suggestion's display name into `?q=`.
 */
const REDACTED_PARAMS = ["q"];

const REDACTED = "REDACTED";

/** Origin for resolving a path-only URL, never part of a redacted result. */
const RELATIVE_BASE = "http://relative.invalid";

/**
 * The URL parsed, plus whether it had to be resolved against
 * {@link RELATIVE_BASE} — a path-only URL has to be rebuilt path-only, since
 * that is the shape the field it came from carries. `undefined` when neither
 * parse lands.
 *
 * @param {string} url
 * @returns {{ parsed: URL, relative: boolean } | undefined}
 */
const parseMonitoredUrl = (url) => {
  try {
    return { parsed: new URL(url), relative: false };
  } catch {
    try {
      return { parsed: new URL(url, RELATIVE_BASE), relative: true };
    } catch {
      return;
    }
  }
};

/**
 * The URL with every {@link REDACTED_PARAMS} value replaced, so a viewer-entered
 * name in a query param stays out of a monitoring payload. Every other param
 * survives so views still group by route; an unparseable value passes through
 * untouched. Absolute and path-only URLs are both handled, and each keeps its
 * own shape — breadcrumb navigation fields are path-only.
 *
 * This covers URL-shaped fields only. Text a monitoring SDK reads off the DOM —
 * Sentry click breadcrumbs, Datadog RUM action names — can still carry an
 * entity name from a row's `aria-label`.
 *
 * @param {string} [url]
 * @returns {string | undefined}
 */
export const redactMonitoredUrl = (url) => {
  if (!url) {
    return url;
  }
  const target = parseMonitoredUrl(url);
  if (!target) {
    return url;
  }
  const { parsed, relative } = target;
  const present = REDACTED_PARAMS.filter((param) =>
    parsed.searchParams.has(param)
  );
  if (present.length === 0) {
    return url;
  }
  for (const param of present) {
    parsed.searchParams.set(param, REDACTED);
  }
  return relative
    ? `${parsed.pathname}${parsed.search}${parsed.hash}`
    : parsed.toString();
};

/**
 * Breadcrumb `data` fields holding a URL: `to`/`from` on a navigation crumb,
 * `url` on an xhr or fetch crumb.
 */
const BREADCRUMB_URL_KEYS = ["to", "from", "url"];

/**
 * A Sentry event with every URL it carries redacted — the request URL and the
 * URLs on its breadcrumbs, which ride along on every captured error.
 *
 * Redacting here covers error events only, which is every envelope this client
 * sends: Session Replay and tracing are both off. Both would defeat it —
 * Replay ships breadcrumbs in its own envelope, and transaction events route
 * through `beforeSendTransaction`, so neither passes `beforeSend`. Enabling
 * either means moving this redaction to a `beforeBreadcrumb` hook, which runs
 * at capture and so covers every path a breadcrumb can leave by.
 *
 * @param {import("@sentry/react").ErrorEvent} event
 * @returns {import("@sentry/react").ErrorEvent}
 */
export const redactMonitoredEvent = (event) => {
  if (event.request?.url) {
    event.request.url = redactMonitoredUrl(event.request.url);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    const data = breadcrumb.data;
    if (!data) {
      continue;
    }
    for (const key of BREADCRUMB_URL_KEYS) {
      if (typeof data[key] === "string") {
        data[key] = redactMonitoredUrl(data[key]);
      }
    }
  }
  return event;
};

const initializeSentry = () => {
  if (parseStringBoolean(process.env.SENTRY_ENABLED)) {
    console.log("================================================");
    console.log("Initializing Sentry");
    console.log("================================================");
    Sentry.init({
      dsn: process.env.SENTRY_DSN,
      environment: process.env.APP_ENVIRONMENT,
      debug: parseStringBoolean(process.env.SENTRY_DEBUG),
      release: dashboardAppVersion,
      tracesSampleRate: 0,
      beforeSend: redactMonitoredEvent,
    });
  }
};

const initializeDatadogRum = () => {
  if (!parseStringBoolean(process.env.DD_RUM_ENABLED)) return;
  console.log("================================================");
  console.log("Initializing Datadog RUM");
  console.log("================================================");
  datadogRum.init({
    applicationId: process.env.DD_RUM_APPLICATION_ID,
    clientToken: process.env.DD_RUM_CLIENT_TOKEN,
    site: process.env.DD_RUM_SITE || "datadoghq.com",
    service: "unified-dashboard",
    env: process.env.APP_ENVIRONMENT,
    version: dashboardAppVersion,
    sessionSampleRate: Number(process.env.DD_RUM_SESSION_SAMPLE_RATE),
    sessionReplaySampleRate: Number(
      process.env.DD_RUM_SESSION_REPLAY_SAMPLE_RATE
    ),
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: (event) => {
      if (event.view) {
        event.view.url = redactMonitoredUrl(event.view.url);
        event.view.referrer = redactMonitoredUrl(event.view.referrer);
      }
      return true;
    },
  });
};

export const initializeObservability = () => {
  initializeSentry();
  initializeDatadogRum();
};
